import {
  alpha,
  AppBar,
  Toolbar,
  styled,
  Button,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
  MenuItem
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { Logo } from "components";
import { usePopup } from "hooks/usePopup";
import { useEffect, useRef, useState } from "react";
import { CgChevronDoubleRight } from "react-icons/cg";
import { FaWarehouse } from "react-icons/fa";
import {
  MdOutlineKeyboardArrowDown,
  MdOutlineKeyboardArrowUp
} from "react-icons/md";
import { RiMenu2Fill } from "react-icons/ri";
import { setDistributionCenterId } from "services/api";
import distributionCenterService from "services/distributionCenterService";
import { MenuPopover } from "shared";
import Cookies from "universal-cookie";

import configLayout from "./ConfigLayout";
import { UserNav } from "./UserNav";

const { APPBAR_DESKTOP, APPBAR_MOBILE } = configLayout;

const ButtonDesktop = styled(Button)(({ theme, open }) => ({
  width: "75px",
  background: "#FAFAFA",
  height: APPBAR_MOBILE,
  [theme.breakpoints.up("sm")]: {
    height: APPBAR_DESKTOP
  },
  "& svg": {
    transform: open ? "rotate(180deg)" : "rotate(0deg)",
    transition: theme.transitions.create("transform", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  }
}));

const ButtomMobile = styled(Button)({
  background: "#FAFAFA",
  marginLeft: "0.7rem",
  borderRadius: "4px",
  padding: "0.3rem 0.4rem",
  minWidth: "initial",
  border: "1px solid #C4C4C4"
});

const RootStyle = styled(AppBar)({
  boxShadow: "none",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  backgroundColor: alpha("#ffffff", 0.72),
  width: "100vw",
  margin: 0
});

const ToolBarStyle = styled(Toolbar)(({ theme }) => ({
  height: APPBAR_MOBILE,
  padding: "0",
  display: "flex",
  alignItems: "center",
  [theme.breakpoints.up("sm")]: {
    height: APPBAR_DESKTOP,
    paddingRight: theme.spacing(5)
  }
}));

const CgChevronDoubleRightStyle = styled(CgChevronDoubleRight)(({ theme }) => ({
  "& path:first-of-type": {
    color: theme.palette.secondary.main
  },
  "& path:last-of-type": {
    color: theme.palette.primary.main
  }
}));

const DistributionCenterButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  "& .MuiButton-label": {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    textAlign: "left",
    color: "#414141"
  },
  "&:hover": {
    backgroundColor: "transparent",
    boxShadow: "none"
  }
}));

const DistributionCenter = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
  const { addPopup } = usePopup();
  const cookies = new Cookies();
  const distributionCenterId = cookies.get("distributionCenterId");
  const [isLoading, setIsLoading] = useState(true);
  const [distributionCenter, setDistributionCenter] = useState(null);
  const [centers, setCenters] = useState([]);
  const buttonRef = useRef(null);
  const [open, setOpen] = useState(false);

  const handleChangeDistributionCenter = center => {
    setDistributionCenter(center);
    setOpen(false);
    setDistributionCenterId(center.id);
    window.location.reload();
  };

  useEffect(() => {
    const getDistributionCenter = async () => {
      try {
        const response =
          await distributionCenterService.getDistributionCenterById(
            distributionCenterId
          );
        setDistributionCenter(response);
      } catch {
        addPopup({
          type: "error",
          title: "Erro ao buscar centro de distribuição"
        });
      } finally {
        setIsLoading(false);
      }
    };
    distributionCenterId && getDistributionCenter();
  }, []);

  useEffect(() => {
    const getCenters = async () => {
      try {
        const response =
          await distributionCenterService.getDistributionCenterByUser();
        setCenters(response);
      } catch {
        addPopup({
          type: "error",
          title: "Erro ao buscar centros de distribuição"
        });
      }
    };
    getCenters();
  }, []);

  return (
    <>
      <DistributionCenterButton
        variant="text"
        disableRipple
        onClick={centers.length > 1 ? () => setOpen(true) : undefined}
        ref={buttonRef}
        style={{ cursor: centers.length > 1 ? "pointer" : "default" }}
      >
        <Box
          style={{
            backgroundColor: theme.palette.primary.main,
            padding: "0.5rem",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center"
          }}
        >
          <FaWarehouse color="#fff" />
        </Box>
        <Box
          style={{
            display: "flex",
            flexDirection: "column"
          }}
        >
          <Typography variant="body2" component="strong">
            <strong>{!isDesktop ? "" : "Centro de distribuição"}</strong>
          </Typography>
          {isLoading ? (
            <Skeleton variant="text" width={100} height={20} />
          ) : (
            <Typography variant={isDesktop ? "body2" : "subtitle2"}>
              {distributionCenter?.name}
            </Typography>
          )}
        </Box>
        {centers.length > 1 && (
          <Box display="flex" flexDirection="column" alignItems="center">
            <MdOutlineKeyboardArrowUp size={13} color="#414141" />
            <MdOutlineKeyboardArrowDown size={13} color="#414141" />
          </Box>
        )}
      </DistributionCenterButton>

      <MenuPopover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={buttonRef.current}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {centers.map(center => (
          <MenuItem
            key={center.id}
            selected={center.id === Number(distributionCenterId)}
            onClick={() => handleChangeDistributionCenter(center)}
            style={{
              padding: "0.7rem",
              fontSize: "0.9rem",
              minWidth: "220px",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem"
            }}
          >
            <Box
              style={{
                backgroundColor: theme.palette.primary.main,
                padding: "0.5rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px"
              }}
            >
              <FaWarehouse color="#fff" />
            </Box>
            <Typography variant="body2">{center.name}</Typography>
          </MenuItem>
        ))}
      </MenuPopover>
    </>
  );
};

export const Navbar = ({ onSidebar, isOpenSidebar }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));

  return (
    <RootStyle position="fixed">
      <ToolBarStyle>
        {isDesktop ? (
          <ButtonDesktop open={isOpenSidebar && isDesktop} onClick={onSidebar}>
            <CgChevronDoubleRightStyle size={30} />
          </ButtonDesktop>
        ) : (
          <ButtomMobile onClick={onSidebar}>
            <RiMenu2Fill color={theme.palette.secondary.main} size={35} />
          </ButtomMobile>
        )}

        <Box
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem"
          }}
        >
          {isDesktop && <Logo style={{ marginLeft: "4rem" }} />}
          <DistributionCenter />
        </Box>

        <UserNav style={{ marginLeft: "auto" }} />
      </ToolBarStyle>
    </RootStyle>
  );
};
